import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Badge, Box, useTheme } from '@mui/material';
import { HouseIcon, MagnifyingGlassIcon, PlusIcon, PopcornIcon } from '@phosphor-icons/react';
import { User } from 'firebase/auth';
import { useUserFollow } from '../../contexts/UserFollowContext';
import { useUserProfileCache } from '../../contexts/UserProfileCacheContext';
import InitialsAvatar from '../widgets/InitialsAvatar';
import { getLastCrewOpenedAt } from '../pages/CrewPage';

interface FloatingSidebarProps {
  value: number;
  onChange: (event: React.SyntheticEvent, newValue: number) => void;
  user?: User;
  getUserInitials?: (displayName?: string | null) => string;
  getInitialsBackgroundColor?: (displayName?: string | null) => string;
  /** Called when the dedicated "+" circle is tapped. Mirrors FloatingBottomNav. */
  onAddPress?: () => void;
}

const FloatingSidebar: React.FC<FloatingSidebarProps> = ({
  value,
  onChange,
  user,
  onAddPress,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const { getUserProfile } = useUserProfileCache();
  const { followingActivity } = useUserFollow();

  // Sliding indicator
  const navContainerRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [indicatorStyle, setIndicatorStyle] = useState({ top: 0, height: 0, opacity: 0 });

  // Crew dot on Home (Home = Crew feed)
  const [hasNewCrew, setHasNewCrew] = useState(false);
  useEffect(() => {
    const last = getLastCrewOpenedAt();
    const hasNew = followingActivity.some((a) => {
      let ts = 0;
      if (typeof a.createdAt === 'string') ts = new Date(a.createdAt).getTime();
      else if ((a.createdAt as any)?.toMillis) ts = (a.createdAt as any).toMillis();
      else if ((a.createdAt as any)?.seconds) ts = (a.createdAt as any).seconds * 1000;
      return ts > last;
    });
    setHasNewCrew(hasNew);
  }, [followingActivity]);

  const userProfile = user?.uid ? getUserProfile(user.uid) : null;
  const userProfileData = useMemo(() => {
    if (!user?.uid) return { avatar: undefined, displayName: 'User' };
    return {
      avatar: userProfile?.photoURL || user.photoURL || undefined,
      displayName: userProfile?.displayName || user.displayName || 'User',
    };
  }, [user?.uid, user?.photoURL, user?.displayName, userProfile?.photoURL, userProfile?.displayName]);

  const navItems: Array<{
    icon: React.ComponentType<any> | null;
    value: number;
    type: 'icon' | 'avatar';
    testId: string;
  }> = [
    { icon: HouseIcon, value: 0, type: 'icon', testId: 'home-tab' },
    { icon: PopcornIcon, value: 1, type: 'icon', testId: 'trending-tab' },
    { icon: MagnifyingGlassIcon, value: 2, type: 'icon', testId: 'search-tab' },
    { icon: null, value: 4, type: 'avatar', testId: 'profile-tab' },
  ];

  useEffect(() => {
    const selectedIdx = value === -1 ? -1 : navItems.findIndex((item) => item.value === value);
    if (selectedIdx === -1) {
      setIndicatorStyle((prev) => ({ ...prev, opacity: 0 }));
      return;
    }
    const containerEl = navContainerRef.current;
    const itemEl = itemRefs.current[selectedIdx];
    if (!containerEl || !itemEl) return;
    const containerRect = containerEl.getBoundingClientRect();
    const itemRect = itemEl.getBoundingClientRect();
    setIndicatorStyle({
      top: itemRect.top - containerRect.top,
      height: itemRect.height,
      opacity: 1,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const handleNavClick = (newValue: number) => {
    onChange({} as React.SyntheticEvent, newValue);
  };

  // Shared liquid-glass styling — matches FloatingBottomNav so the two reads as one family.
  const glassSx = {
    backgroundColor: isDark ? 'rgba(0, 0, 0, 0.75)' : 'rgba(255, 255, 255, 0.55)',
    backgroundImage: isDark
      ? 'linear-gradient(135deg, rgba(255,255,255,0.12) 0%, rgba(255,255,255,0.03) 45%, rgba(255,255,255,0.08) 100%)'
      : 'linear-gradient(135deg, rgba(255,255,255,0.65) 0%, rgba(255,255,255,0.2) 45%, rgba(255,255,255,0.45) 100%)',
    backdropFilter: 'blur(5px)',
    WebkitBackdropFilter: 'blur(5px)',
    border: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.18)' : 'rgba(255, 255, 255, 0.55)'}`,
    boxShadow: isDark
      ? '0 10px 36px rgba(0, 0, 0, 0.5), inset 0 1px 0 rgba(255, 255, 255, 0.18), inset 0 -1px 0 rgba(0, 0, 0, 0.3)'
      : '0 10px 36px rgba(0, 0, 0, 0.18), inset 0 1px 0 rgba(255, 255, 255, 0.85), inset 0 -1px 0 rgba(0, 0, 0, 0.06)',
  };

  return (
    <Box
      sx={{
        position: 'fixed',
        left: 16,
        top: '50%',
        transform: 'translateY(-50%)',
        zIndex: 1200,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 1.25,
      }}
    >
      {/* "+" circle at the top — brand tint to stand out */}
      <Box
        data-testid="sidebar-add"
        onClick={onAddPress}
        sx={{
          width: 60,
          height: 60,
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          position: 'relative',
          backgroundColor: 'rgba(211, 15, 69, 0.88)',
          backgroundImage: 'linear-gradient(135deg, rgba(255,255,255,0.18) 0%, rgba(255,255,255,0) 50%)',
          backdropFilter: 'blur(5px)',
          WebkitBackdropFilter: 'blur(5px)',
          border: '1px solid rgba(255,255,255,0.22)',
          boxShadow: '0 10px 36px rgba(211,15,69,0.4), inset 0 1px 0 rgba(255,255,255,0.25)',
          transition: 'transform 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': { transform: 'scale(1.06)' },
          '&:active': { transform: 'scale(0.94)' },
          '&::before': {
            content: '""',
            position: 'absolute',
            inset: 0,
            borderRadius: 'inherit',
            pointerEvents: 'none',
            background: 'linear-gradient(180deg, rgba(255,255,255,0.12) 0%, rgba(255,255,255,0) 50%)',
            mixBlendMode: 'overlay',
          },
          '& > *': { position: 'relative', zIndex: 1 },
        }}
      >
        <PlusIcon size={30} color="rgba(255,255,255,0.95)" />
      </Box>

      {/* Vertical pill: Home / Search / Avatar */}
      <Box
        ref={navContainerRef}
        data-testid="sidebar-nav"
        sx={{
          position: 'relative',
          borderRadius: '32px',
          px: 1,
          py: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 0.75,
          ...glassSx,
          '&::before': {
            content: '""',
            position: 'absolute',
            inset: 0,
            borderRadius: 'inherit',
            pointerEvents: 'none',
            background: isDark
              ? 'linear-gradient(180deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0) 38%)'
              : 'linear-gradient(180deg, rgba(255,255,255,0.2) 0%, rgba(255,255,255,0) 45%)',
            mixBlendMode: 'overlay',
          },
          '&::after': {
            content: '""',
            position: 'absolute',
            inset: 0,
            borderRadius: 'inherit',
            pointerEvents: 'none',
            background: isDark
              ? 'radial-gradient(60% 120% at 100% 50%, rgba(124, 105, 250, 0.12) 0%, rgba(0,0,0,0) 60%)'
              : 'radial-gradient(60% 120% at 100% 50%, rgba(54, 52, 163, 0.08) 0%, rgba(255,255,255,0) 60%)',
          },
          '& > *': { position: 'relative', zIndex: 1 },
        }}
      >
        {/* Sliding indicator — moves up/down */}
        <Box
          sx={{
            position: 'absolute',
            left: '50%',
            transform: 'translateX(-50%)',
            top: indicatorStyle.top,
            height: indicatorStyle.height,
            width: 'calc(100% - 12px)',
            bgcolor: isDark ? 'rgba(146, 129, 255, 0.31)' : 'rgba(54, 52, 163, 0.1)',
            borderRadius: 100,
            opacity: indicatorStyle.opacity,
            pointerEvents: 'none',
            zIndex: 0,
            transition:
              'top 0.28s cubic-bezier(0.4, 0, 0.2, 1), height 0.28s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.18s ease',
          }}
        />

        {navItems.map((item, idx) => {
          const isSelected = value !== -1 && value === item.value;
          const Icon = item.icon;

          return (
            <Box
              key={item.value}
              ref={(el: HTMLDivElement | null) => {
                itemRefs.current[idx] = el;
              }}
              data-testid={item.testId}
              onClick={() => handleNavClick(item.value)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 48,
                height: 48,
                cursor: 'pointer',
                borderRadius: 100,
                transition: 'transform 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                bgcolor: 'transparent',
                '&:hover': isSelected ? {} : {
                  bgcolor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)',
                  transform: 'scale(1.08)',
                },
                '&:active': { transform: 'scale(0.95)' },
              }}
            >
              {item.type === 'avatar' ? (
                <InitialsAvatar
                  src={userProfileData.avatar}
                  fallbackSrc={user?.photoURL || undefined}
                  name={userProfileData.displayName}
                  size={isSelected ? 28 : 26}
                  sx={{
                    transition: 'all 0.2s ease',
                    border: isSelected
                      ? `2px solid ${theme.palette.primary.main}`
                      : `2px solid ${isDark ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)'}`,
                    transform: isSelected ? 'scale(1.1)' : 'scale(1)',
                    boxShadow: isSelected
                      ? isDark
                        ? '0 0 0 2px rgba(125, 122, 255, 0.3)'
                        : '0 0 0 2px rgba(54, 52, 163, 0.3)'
                      : 'none',
                  }}
                />
              ) : (
                Icon && (
                  item.value === 0 ? (
                    <Badge
                      variant="dot"
                      invisible={!hasNewCrew}
                      sx={{
                        '& .MuiBadge-dot': {
                          backgroundColor: '#FF5722',
                          width: 10,
                          height: 10,
                          borderRadius: '50%',
                          border: `2px solid ${isDark ? '#2C2C2E' : '#FFFFFF'}`,
                        },
                      }}
                    >
                      <Icon
                        size={26}
                        color={isSelected ? theme.palette.common.white : isDark ? 'rgba(255,255,255,0.85)' : 'rgba(60,60,67,0.85)'}
                        weight={isSelected ? 'duotone' : 'regular'}
                      />
                    </Badge>
                  ) : (
                    <Icon
                      size={26}
                      color={isSelected ? theme.palette.common.white : isDark ? 'rgba(255,255,255,0.85)' : 'rgba(60,60,67,0.85)'}
                      weight={item.value === 1 && isSelected ? 'fill' : isSelected ? 'duotone' : 'regular'}
                    />
                  )
                )
              )}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export default FloatingSidebar;
